import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import cm from 'classnames';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Tree } from 'ksnm-common-ui/lib/Tree';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { useCheckFromDictionaryCard } from '@src/utils/hooks/useCheckFromDictionaryCard';
import { usePermission } from '@src/utils/hooks/usePermission';
import { recursiveFindByCode } from '@src/utils/tree';
import { controlApplicabilityStore } from '@stores/dictionaryStores/controlApplicabilityStore/controlApplicabilityStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { organizationsStore } from '@stores/dictionaryStores/organizationsStore';
import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { useAuditView } from '../../hooks/useAuditView';
import { Filter } from './Filter';
import { Table } from './Table';
import { TemplateModal } from './TemplateModal';
import { toTreeView } from './toTreeView';

import styles from './ControlApplicability.module.scss';

export const ControlApplicability = observer(() => {
  const {
    fetch,
    controlApplicability,
    filters,
    setBreadcrumbs,
    fullSize,
    selectNode,
    setSelectNode,
    selectedKeys,
    setSelectedKeys,
    expandedKeys,
    setExpandedKeys,
    toggleTemplateModal,
    clear,
  } = controlApplicabilityStore;
  const { push } = useHistory();
  const { reportGroups } = reportGroupsStore;
  const { organizations } = organizationsStore;

  const permission = usePermission('CmControlApplicability');

  const isBack = useCheckFromDictionaryCard(RoutePath.DICTIONARY_CONTROL_APPLICABILITY);

  useAuditView('Применимости контролей');

  useEffect(() => {
    void (async () => {
      await fetch();
    })();
    fetchReferences(['reportGroups', 'organizations']);
  }, [fetch]);

  useEffect(() => {
    if (!isBack) clear();
  }, [clear, isBack]);

  const treeData = useMemo(
    () =>
      toTreeView(
        controlApplicability.filter(
          (control) =>
            !(
              (filters.reportGroup && control.repGroupCode !== filters.reportGroup) ||
              (filters.organization && control.orgCode !== filters.organization)
            ),
        ),
        reportGroups,
        organizations,
      ),
    [controlApplicability, reportGroups, organizations, filters.reportGroup, filters.organization],
  );

  const onSelect = useCallback(
    (_, { node, selected }) => {
      if (selected) {
        setSelectNode(node);
        setSelectedKeys([node.key]);

        const nodes = recursiveFindByCode(node.key, treeData);
        if (nodes) setBreadcrumbs(nodes.map((item) => item.title));
      }
    },
    [setBreadcrumbs, setSelectNode, setSelectedKeys, treeData],
  );

  const onExpand = useCallback(
    (keys) => {
      setExpandedKeys(keys);
    },
    [setExpandedKeys],
  );

  const onCreate = useCallback(() => {
    push(`${RoutePath.DICTIONARY_CONTROL_APPLICABILITY}/${NEW_ENTITY_ID}`);
  }, [push]);

  const actions = useMemo(
    () => (
      <>
        {!selectNode && permission?.CREATE && (
          <Button onClick={onCreate} tooltip="Добавить">
            <Icon name="add" />
          </Button>
        )}
        {!selectNode && (
          <Button onClick={toggleTemplateModal} key="add" tooltip="Добавить шаблон">
            <Icon name="addDocument" />
          </Button>
        )}
      </>
    ),
    [selectNode, permission?.CREATE, onCreate, toggleTemplateModal],
  );

  return (
    <DesignLayout scrollEnable>
      <Filter />
      <div className={styles.container}>
        <Tree
          selectable
          treeData={treeData}
          onSelect={onSelect}
          className={cm(styles.tree, { [styles.withTable]: selectNode, [styles.fullSize]: fullSize })}
          selectedKeys={selectedKeys}
          expandedKeys={expandedKeys}
          onExpand={onExpand}
          actions={actions}
        />
        <Table />
      </div>
      <TemplateModal />
    </DesignLayout>
  );
});
